import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { useWeb3 } from '../contexts/Web3Context';
import { Loader2, ArrowLeft, ExternalLink, Clock, Wallet, AlertTriangle, TrendingUp, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getEscrowContract, getPayoutContract, formatAddress, calculateExpectedYield, calculateAPY } from '../utils/contracts';
import { ethers } from 'ethers';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { account, signer, isConnected } = useWeb3();
  const [invoice, setInvoice] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [contractAddresses, setContractAddresses] = useState(null);

  useEffect(() => {
    fetchInvoiceDetails();
    fetchContractAddresses();
  }, [id]);

  const fetchContractAddresses = async () => {
    try {
      const response = await axios.get(`${API}/config/contracts`);
      setContractAddresses(response.data);
    } catch (error) {
      console.error('Error fetching contract addresses:', error);
    }
  };

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const invoiceResponse = await axios.get(`${API}/invoices/${id}`);
      setInvoice(invoiceResponse.data);

      const transactionsResponse = await axios.get(`${API}/transactions/invoice/${id}`);
      setTransactions(transactionsResponse.data);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to fetch invoice details');
    } finally {
      setLoading(false);
    }
  };

  const handleFinance = async () => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!contractAddresses?.escrow) {
      toast.error('Contract addresses not configured');
      return;
    }

    try {
      setProcessing(true);

      const escrowContract = getEscrowContract(signer, contractAddresses.escrow);
      const amountInWei = ethers.parseEther(invoice.amount.toString());

      toast.info('Waiting for transaction confirmation...');

      const tx = await escrowContract.acceptFinancing(invoice.token_id, {
        value: amountInWei,
      });

      toast.info('Transaction submitted. Waiting for confirmation...');
      const receipt = await tx.wait();

      await axios.put(`${API}/invoices/${invoice.id}`, {
        status: 'FUNDED',
      });

      await axios.post(`${API}/transactions`, {
        invoice_id: invoice.token_id.toString(),
        transaction_hash: receipt.hash,
        transaction_type: 'FINANCE',
        from_address: account.toLowerCase(),
        to_address: invoice.msme_address,
        amount: invoice.amount,
      });

      toast.success('Invoice financed successfully!');
      fetchInvoiceDetails();
    } catch (error) {
      console.error('Error financing invoice:', error);
      toast.error(error.message || 'Failed to finance invoice');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="App min-h-screen">
        <Navbar />
        <div data-testid="loading-spinner" className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="App min-h-screen">
        <Navbar />
        <div className="container mx-auto px-6 py-24">
          <div className="text-center">
            <p className="text-slate-600">Invoice not found</p>
            <Button onClick={() => navigate('/marketplace')} className="mt-4">
              Back to Marketplace
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const getRiskColor = (risk) => {
    if (risk <= 3) return 'text-emerald-600 border-emerald-200 bg-emerald-50';
    if (risk <= 6) return 'text-amber-600 border-amber-200 bg-amber-50';
    return 'text-red-600 border-red-200 bg-red-50';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'LISTED':
        return 'text-blue-600 border-blue-200 bg-blue-50';
      case 'FUNDED':
        return 'text-amber-600 border-amber-200 bg-amber-50';
      case 'PAID':
        return 'text-emerald-600 border-emerald-200 bg-emerald-50';
      case 'DEFAULTED':
        return 'text-red-600 border-red-200 bg-red-50';
      default:
        return 'text-slate-600 border-slate-200 bg-slate-50';
    }
  };

  const expectedYield = calculateExpectedYield(invoice.amount, invoice.risk_score);
  const apy = calculateAPY(invoice.risk_score);
  const totalPayout = invoice.amount + expectedYield;

  return (
    <div className="App min-h-screen">
      <Navbar />
      
      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          variant="outline"
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div data-testid="invoice-header" className="professional-card p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-indigo-600" />
                    <span className="text-sm font-medium uppercase tracking-widest text-slate-600">
                      Invoice #{invoice.invoice_number || invoice.token_id}
                    </span>
                  </div>
                  <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {invoice.company_name || 'Business Invoice'}
                  </h1>
                </div>
                <div className={`rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider border ${getStatusColor(invoice.status)}`}>
                  {invoice.status}
                </div>
              </div>

              {invoice.description && (
                <p className="text-slate-600 mb-6">{invoice.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-xs text-slate-600 uppercase tracking-wider mb-2">Amount</div>
                  <div className="text-2xl font-bold font-mono">{invoice.amount} ETH</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 uppercase tracking-wider mb-2">APY</div>
                  <div className="text-2xl font-bold text-indigo-600">{apy}%</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 uppercase tracking-wider mb-2">Yield</div>
                  <div className="text-2xl font-bold text-emerald-500">{expectedYield.toFixed(4)} ETH</div>
                </div>
                <div>
                  <div className="text-xs text-slate-600 uppercase tracking-wider mb-2">Total</div>
                  <div className="text-2xl font-bold">{totalPayout.toFixed(4)} ETH</div>
                </div>
              </div>
            </div>

            <div data-testid="invoice-details" className="professional-card p-8">
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Invoice Details
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="h-4 w-4" />
                    <span>Due Date</span>
                  </div>
                  <span className="font-medium">{format(new Date(invoice.due_date), 'MMMM dd, yyyy')}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="flex items-center gap-2 text-slate-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>Risk Score</span>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider border ${getRiskColor(invoice.risk_score)}`}>
                    {invoice.risk_score}/10
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Wallet className="h-4 w-4" />
                    <span>MSME Address</span>
                  </div>
                  <span className="font-mono text-sm">{formatAddress(invoice.msme_address)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Wallet className="h-4 w-4" />
                    <span>Debtor Address</span>
                  </div>
                  <span className="font-mono text-sm">{formatAddress(invoice.debtor_address)}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <ExternalLink className="h-4 w-4" />
                    <span>IPFS Document</span>
                  </div>
                  <Button
                    data-testid="view-ipfs-button"
                    variant="outline"
                    size="sm"
                    onClick={() => toast.info('IPFS viewing is mocked in this demo')}
                  >
                    View Document
                  </Button>
                </div>
              </div>
            </div>

            {transactions.length > 0 && (
              <div data-testid="transactions-section" className="professional-card p-8">
                <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Transaction History
                </h2>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
                      <div>
                        <div className="font-medium">{tx.transaction_type}</div>
                        <div className="text-xs text-slate-600 font-mono">{formatAddress(tx.transaction_hash)}</div>
                      </div>
                      <div className="text-sm text-slate-600">
                        {format(new Date(tx.timestamp), 'MMM dd, HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div data-testid="action-card" className="professional-card p-6 sticky top-24">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Investment Summary
              </h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Investment</span>
                  <span className="font-bold font-mono">{invoice.amount} ETH</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Expected Yield</span>
                  <span className="font-bold font-mono text-emerald-500">+{expectedYield.toFixed(4)} ETH</span>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="font-medium">Total Return</span>
                  <span className="font-bold font-mono text-lg">{totalPayout.toFixed(4)} ETH</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">APY</span>
                  <span className="font-bold text-indigo-600 text-lg">{apy}%</span>
                </div>
              </div>

              {invoice.status === 'LISTED' && isConnected && account !== invoice.msme_address && (
                <Button
                  data-testid="finance-invoice-button"
                  onClick={handleFinance}
                  disabled={processing}
                  className="w-full bg-primary text-indigo-600-foreground hover:bg-primary/90 font-bold uppercase tracking-wide button-scale"
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Finance Invoice
                    </>
                  )}
                </Button>
              )}

              {!isConnected && (
                <Button
                  data-testid="connect-prompt"
                  onClick={() => toast.info('Please connect your wallet from the navbar')}
                  className="w-full"
                  variant="outline"
                >
                  Connect Wallet to Finance
                </Button>
              )}
            </div>

            <div data-testid="risk-info" className="professional-card p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Risk Breakdown
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Credit Score</span>
                  <span className="font-medium">{invoice.risk_score}/10</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Payment History</span>
                  <span className="font-medium">{invoice.risk_score <= 3 ? 'Excellent' : invoice.risk_score <= 6 ? 'Good' : 'Fair'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Debtor Verified</span>
                  <span className="font-medium">Yes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};